/**
 * Portal Init API Transformers
 * Transforms API DTOs to domain types
 *
 * Note: The /api/users/me endpoint returns data that maps directly to domain types,
 * so transformations are minimal - mostly just type assertion after Zod validation.
 */

import type {
  InitData,
  GuestInitData,
  EcommerceInitData,
  CustomerInitData,
  AdminInitData,
} from '../domain/types'
import type {
  MeResponseDto,
  GuestResponseDto,
  EcommerceResponseDto,
  CustomerResponseDto,
  AdminResponseDto,
} from './api-types'

/**
 * Transform /api/users/me response to domain type
 * The API returns discriminated union with 'type' field
 */
export function transformMeResponse(response: MeResponseDto): InitData {
  switch (response.type) {
    case 'guest':
      return transformGuestResponse(response)
    case 'ecommerce':
      return transformEcommerceResponse(response)
    case 'customer':
      return transformCustomerResponse(response)
    case 'admin':
      return transformAdminResponse(response)
  }
}

/**
 * Transform guest response
 */
export function transformGuestResponse(dto: GuestResponseDto): GuestInitData {
  return {
    type: 'guest',
    featureFlags: dto.featureFlags,
    ecommerce: {
      priceList: dto.ecommerce.priceList,
    },
  }
}

/**
 * Transform ecommerce user response
 */
export function transformEcommerceResponse(dto: EcommerceResponseDto): EcommerceInitData {
  return {
    type: 'ecommerce',
    profile: dto.profile,
    pricingProfile: dto.pricingProfile,
    featureFlags: dto.featureFlags,
  }
}

/**
 * Transform customer user response
 */
export function transformCustomerResponse(dto: CustomerResponseDto): CustomerInitData {
  return {
    type: 'customer',
    profile: dto.profile,
    customerAccount: dto.customerAccount,
    settings: dto.settings,
    featureFlags: dto.featureFlags,
  }
}

/**
 * Transform admin user response
 */
export function transformAdminResponse(dto: AdminResponseDto): AdminInitData {
  return {
    type: 'admin',
    profile: dto.profile,
    permissions: dto.permissions,
    featureFlags: dto.featureFlags,
  }
}

// Legacy export for backwards compatibility
export const transformInitResponse = transformMeResponse
